"use client";

import React, { useState, useRef, useEffect } from "react";
import {
  motion,
  AnimatePresence,
  useInView,
  useMotionValue,
  useTransform,
  useSpring,
  animate,
} from "motion/react";
import { Card, Chip } from "@heroui/react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  CodeIcon,
  Database01Icon,
  AnalyticsUpIcon,
  CloudIcon,
  ArrowUpRightIcon,
} from "@hugeicons/core-free-icons";

// ─── Data ────────────────────────────────────────────────────────────────────

const categories = [
  {
    key: "frontend",
    label: "Frontend",
    icon: CodeIcon,
    color: {
      accent: "sky",
      pill: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
      dot: "bg-sky-400",
      badge: "bg-sky-50 text-sky-600 border border-sky-200/50 dark:bg-sky-950/40 dark:text-sky-450 dark:border-sky-900/30",
      ring: "ring-sky-500/20 dark:ring-sky-400/10",
      glow: "from-sky-500/10 to-blue-500/10 dark:from-sky-500/15 dark:to-blue-500/5",
      borderGlow: "group-hover:border-sky-500/40 dark:group-hover:border-sky-400/30",
      badgeActive: "bg-sky-500 text-white",
    },
    description: "Modern client-side architecture for intuitive, high-performance experiences.",
    technologies: [
      { id: "react", name: "React.js", tag: "UI Library", desc: "Hooks, context, Zustand & Redux for dynamic, performant user interfaces." },
      { id: "nextjs", name: "Next.js", tag: "Framework", desc: "SSR & SSG applications optimised for performance and SEO." },
      { id: "gatsby", name: "Gatsby.js", tag: "Static Sites", desc: "Fast static sites powered by GraphQL data layer and a rich plugin ecosystem." },
      { id: "vuejs", name: "Vue.js", tag: "Progressive", desc: "Scalable apps with Vuex state management and Vue Router." },
      { id: "html5", name: "HTML5", tag: "Markup", desc: "Semantic, accessible, search-engine-friendly document structure." },
      { id: "css3", name: "CSS3 & Sass", tag: "Styling", desc: "Flexbox, Grid, animations, and maintainable preprocessor architectures." },
      { id: "tailwind", name: "Tailwind CSS", tag: "Utility-First", desc: "Rapid custom UI without context-switching from HTML." },
      { id: "vite", name: "Vite", tag: "Build Tool", desc: "Instant dev server start and blazing-fast hot module replacement." },
    ],
  },
  {
    key: "backend",
    label: "Backend",
    icon: Database01Icon,
    color: {
      accent: "emerald",
      pill: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
      dot: "bg-emerald-400",
      badge: "bg-emerald-50 text-emerald-600 border border-emerald-200/50 dark:bg-emerald-950/40 dark:text-emerald-450 dark:border-emerald-900/30",
      ring: "ring-emerald-500/20 dark:ring-emerald-400/10",
      glow: "from-emerald-500/10 to-teal-500/10 dark:from-emerald-500/15 dark:to-teal-500/5",
      borderGlow: "group-hover:border-emerald-500/40 dark:group-hover:border-emerald-400/30",
      badgeActive: "bg-emerald-500 text-white",
    },
    description: "Scalable server-side logic and type-safe languages powering enterprise applications.",
    technologies: [
      { id: "js", name: "JavaScript ES6+", tag: "Core Language", desc: "Async/await, modules, and functional patterns in modern JS." },
      { id: "ts", name: "TypeScript", tag: "Type Safety", desc: "Large-scale codebases with full type coverage for reliability." },
      { id: "nodejs", name: "Node.js", tag: "Runtime", desc: "Event-driven backend services via Express.js and NestJS." },
      { id: "java", name: "Java / Spring", tag: "Enterprise", desc: "Robust and secure systems for high-traffic enterprise backends." },
    ],
  },
  {
    key: "data",
    label: "Data & APIs",
    icon: AnalyticsUpIcon,
    color: {
      accent: "violet",
      pill: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
      dot: "bg-violet-400",
      badge: "bg-violet-50 text-violet-600 border border-violet-200/50 dark:bg-violet-950/40 dark:text-violet-450 dark:border-violet-900/30",
      ring: "ring-violet-500/20 dark:ring-violet-400/10",
      glow: "from-violet-500/10 to-indigo-500/10 dark:from-violet-500/15 dark:to-indigo-500/5",
      borderGlow: "group-hover:border-violet-500/40 dark:group-hover:border-violet-400/30",
      badgeActive: "bg-violet-500 text-white",
    },
    description: "Robust data models and efficient communication protocols for modern web services.",
    technologies: [
      { id: "mongo", name: "MongoDB", tag: "NoSQL DB", desc: "Flexible schema-less data modelling for dynamic applications." },
      { id: "rest", name: "REST APIs", tag: "Standard", desc: "Clean, documented, and scalable RESTful API design." },
      { id: "graphql", name: "GraphQL", tag: "Query Language", desc: "Apollo Server & Client for efficient, flexible data fetching." },
    ],
  },
  {
    key: "infra",
    label: "Infrastructure",
    icon: CloudIcon,
    color: {
      accent: "amber",
      pill: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
      dot: "bg-amber-400",
      badge: "bg-amber-50 text-amber-600 border border-amber-200/50 dark:bg-amber-950/40 dark:text-amber-450 dark:border-amber-900/30",
      ring: "ring-amber-500/20 dark:ring-amber-400/10",
      glow: "from-amber-500/10 to-orange-500/10 dark:from-amber-500/15 dark:to-orange-500/5",
      borderGlow: "group-hover:border-amber-500/40 dark:group-hover:border-amber-400/30",
      badgeActive: "bg-amber-500 text-white",
    },
    description: "Cloud deployment and backend-as-a-service for reliable, always-on applications.",
    technologies: [
      { id: "firebase", name: "Firebase", tag: "BaaS", desc: "Real-time Firestore, Auth, and Cloud Functions for live apps." },
      { id: "appwrite", name: "Appwrite", tag: "Open Source BaaS", desc: "Self-hosted backend with auth, storage, and serverless functions." },
      { id: "aws", name: "AWS", tag: "Cloud", desc: "EC2, S3, Lambda, and RDS for scalable production deployments." },
      { id: "gcp", name: "Google Cloud", tag: "Cloud", desc: "Compute Engine, Cloud Functions, and Firestore at scale." },
    ],
  },
];

// ─── Sub-components ──────────────────────────────────────────────────────────

interface TechItem {
  id: string;
  name: string;
  tag: string;
  desc: string;
}

interface CategoryColor {
  accent: string;
  pill: string;
  dot: string;
  badge: string;
  ring: string;
  glow: string;
  borderGlow: string;
  badgeActive: string;
}

interface TechCardProps {
  tech: TechItem;
  color: CategoryColor;
  index: number;
}

function TechCard({ tech, color, index }: TechCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  
  // Interactive 3D mouse hover effect parameters
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [10, -10]), { stiffness: 150, damping: 20 });
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-10, 10]), { stiffness: 150, damping: 20 });
  
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const x = (e.clientX - rect.left) / width - 0.5;
    const y = (e.clientY - rect.top) / height - 0.5;
    mouseX.set(x);
    mouseY.set(y);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
      className="group "
    >
      <motion.div
        ref={cardRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{
          rotateX,
          rotateY,
          transformPerspective: 1000,
        }}
        className="h-full cursor-pointer select-none"
      >
        <Card
          variant="transparent"
          className={`
            h-full rounded-4xl p-6 relative overflow-hidden border-2
            bg-blue-600/5 backdrop-blur-sm border-blue-800 dark:bg-neutral-900/20
            transition-all duration-500 ease-out
            hover:bg-white/80 dark:hover:bg-neutral-900/40
            hover:shadow-xl hover:shadow-neutral-200/20 dark:hover:shadow-none
            
          `}
        >
          {/* Subtle top glow overlay */}
          <div className={`absolute -top-16 -right-16 w-32 h-32 rounded-full blur-[40px] opacity-0 group-hover:opacity-40 transition-opacity duration-500 bg-gradient-to-br ${color.glow}`} />

          {/* Top row */}
          <Card.Header className="p-0 mb-4 flex items-start justify-between gap-3">
            <Card.Title className="text-lg font-bold tracking-tight text-neutral-800 dark:text-neutral-100 leading-snug font-outfit">
              {tech.name}
            </Card.Title>
            <span className={`shrink-0 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-lg ${color.badge}`}>
              {tech.tag}
            </span>
          </Card.Header>

          {/* Description */}
          <Card.Content className="p-0 flex-grow">
            <p className="text-sm leading-relaxed text-neutral-500 dark:text-neutral-400 font-outfit">
              {tech.desc}
            </p>
          </Card.Content>

          {/* Interactive Arrow Link Accent */}
          <div className="mt-5 flex items-center justify-between">
            <span className="text-xs font-semibold text-neutral-450 dark:text-neutral-550 group-hover:text-neutral-800 dark:group-hover:text-neutral-300 transition-colors duration-300">
              Expertise
            </span>
            <motion.span
              className={`p-1.5 rounded-lg border border-neutral-200/40 dark:border-neutral-800/60 text-neutral-400 group-hover:text-neutral-800 dark:group-hover:text-neutral-250 transition-colors duration-300`}
              whileHover={{ rotate: 45 }}
              transition={{ type: "spring", stiffness: 300, damping: 15 }}
            >
              <HugeiconsIcon icon={ArrowUpRightIcon} size={14} />
            </motion.span>
          </div>
        </Card>
      </motion.div>
    </motion.div>
  );
}

function Counter({ value }: { value: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const [displayValue, setDisplayValue] = useState("0");

  useEffect(() => {
    if (isInView) {
      const num = parseInt(value.replace(/[^0-9]/g, ""), 10);
      const suffix = value.replace(/[0-9]/g, "");
      const controls = animate(0, num, {
        duration: 1.8,
        ease: [0.16, 1, 0.3, 1], // premium custom cubic-bezier easeOut
        onUpdate: (latest) => {
          setDisplayValue(Math.floor(latest) + suffix);
        },
      });
      return () => controls.stop();
    }
  }, [isInView, value]);

  return <span ref={ref}>{displayValue}</span>;
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function Expertise() {
  const [active, setActive] = useState("frontend");
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true, margin: "-80px" });

  const activeCat = categories.find((c) => c.key === active) || categories[0];

  return (
    <section className="relative min-h-screen w-full py-28 px-4 md:px-8 overflow-hidden bg-neutral-50 dark:bg-black transition-colors duration-500">

      {/* Dynamic background gradient blobs following active category */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className={`absolute top-[-10%] left-[25%] w-[600px] h-[600px] rounded-full blur-[140px] opacity-40 bg-gradient-to-br ${activeCat.color.glow} transition-all duration-1000`} />
        <div className="absolute bottom-[-10%] right-[5%] w-[450px] h-[450px] rounded-full blur-[120px] opacity-25 bg-gradient-to-br from-neutral-200/20 to-transparent dark:from-neutral-800/10" />
      </div>

      {/* Modern thin dotted pattern background */}
      <div
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.03] dark:opacity-[0.05]"
        style={{
          backgroundImage: "radial-gradient(circle, #64748b 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      {/* ── Header ── */}
      <div ref={headerRef} className="text-center mb-16 max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={headerInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mb-5 flex justify-center"
        >
          <Chip
            size="sm"
            variant="soft"
            className="bg-neutral-200/40 dark:bg-neutral-900/40 text-[10px] uppercase tracking-widest font-extrabold text-neutral-500 dark:text-neutral-400 px-4 py-1.5 border border-neutral-300/20 dark:border-neutral-800/30 rounded-full"
          >
            Expertise & Capabilities
          </Chip>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={headerInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.65, delay: 0.1, ease: "easeOut" }}
          className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight font-recoleta text-neutral-850 dark:text-neutral-50 mb-6"
        >
          Our Specialized{" "}
          <span className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 bg-clip-text text-transparent">
            Tech Stack
          </span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 15 }}
          animate={headerInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          className="text-neutral-500 dark:text-neutral-400 text-base sm:text-lg leading-relaxed font-outfit max-w-2xl mx-auto"
        >
          Battle-tested tools and frameworks we use to ship exceptional digital products — from clean architecture to serverless deployment.
        </motion.p>
      </div>

      {/* ── Custom sliding navigation tab bar ── */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={headerInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5, delay: 0.25 }}
        className="flex justify-center mb-8"
      >
        <div className="inline-flex items-center gap-1.5 p-2 rounded-full bg-white/7 dark:bg-black/7 backdrop-blur-sm border border-white/[0.18] shadow-[0px_8px_32px_-8px_rgba(0,0,0,0.3)] overflow-x-auto scrollbar-hidden">
          {categories.map((cat) => {
            const isActive = active === cat.key;
            return (
              <motion.button
                key={cat.key}
                onClick={() => setActive(cat.key)}
                whileTap={{ scale: 0.97 }}
                className={`
                  relative flex items-center gap-2.5 px-5 py-3 rounded-full text-sm font-semibold font-outfit
                  transition-colors duration-300 outline-none cursor-pointer select-none
                  ${isActive
                    ? "text-neutral-900 dark:text-neutral-50"
                    : "text-neutral-500 dark:text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-350"
                  }
                `}
              >
                {isActive && (
                  <motion.span
                    layoutId="active-tab-glow"
                    className="absolute inset-0 rounded-full bg-white dark:bg-neutral-900 border border-neutral-200/50 dark:border-neutral-800/40 shadow-sm z-0"
                    transition={{ type: "spring", stiffness: 350, damping: 28 }}
                  />
                )}
                
                <span className="relative z-10 flex items-center justify-center">
                  <HugeiconsIcon
                    icon={cat.icon}
                    size={18}
                    className={`transition-colors duration-300 ${isActive ? `text-${cat.color.accent}-500 dark:text-${cat.color.accent}-400` : "text-neutral-400"}`}
                  />
                </span>
                
                <span className="relative z-10 whitespace-nowrap">{cat.label}</span>
              </motion.button>
            );
          })}
        </div>
      </motion.div>

      {/* ── Category Description with exit/entry motion ── */}
      <div className="flex justify-center mb-14 min-h-[3rem]">
        <AnimatePresence mode="wait">
          <motion.p
            key={active}
            initial={{ opacity: 0, y: 8, filter: "blur(4px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: -8, filter: "blur(4px)" }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="text-base text-neutral-500 dark:text-neutral-400 font-outfit max-w-xl text-center leading-relaxed"
          >
            {activeCat.description}
          </motion.p>
        </AnimatePresence>
      </div>

      {/* ── Tech Cards Grid ── */}
      <div className="xl:px-32 lg:px-24 md:px-16 sm:px-4 px-2 mx-auto w-full">
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
          >
            {activeCat.technologies.map((tech, i) => (
              <TechCard
                key={tech.id}
                tech={tech}
                color={activeCat.color}
                index={i}
              />
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}